// Guarantor insolvency on the Case progress header (Dean, Oct 2026): Log bankruptcy / IVA does the map work in the same step;
// the "Insolvency: N active" chip lists what is logged and lets you mark one discharged (resolving its sub-timeline) or remove it.
import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, Trash2, UserX } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { ukDate } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { activeInsolvencies } from "@/components/collections/PgInsolvency";
import { today } from "./mapShared";

const err = (e, fallback) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : d?.message || fallback; };

export function LogInsolvencyDialog({ caseRow, map, onClose, onSaved }) {
  const [types, setTypes] = useState([]);
  const people = (map?.parties || []).filter((p) => p.type === "individual" && !p.hidden).map((p) => p.name);
  const [form, setForm] = useState({ guarantor_name: people[0] || "", insolvency_type: "bankruptcy", case_reference: "", date: "", practitioner: "", notes: "", map: true });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/legal/pg-insolvency/types").then((r) => setTypes(r.data.types || [])).catch(() => {}); }, []);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const hint = types.find((t) => t.key === form.insolvency_type)?.map_hint;
  const save = async () => {
    if (form.guarantor_name.trim().length < 2 || !form.date) { toast.error("Guarantor and date are required"); return; }
    setBusy(true);
    try {
      const r = (await api.post(`/legal/pg-insolvency/${caseRow.case_id}`, form)).data;
      if (r.map?.ok) toast.success(`Insolvency logged — ${r.map.summary}`);
      else if (r.map?.error) toast.warning(`Insolvency logged, but the map could not be updated: ${r.map.error}`);
      else toast.success("Insolvency logged — letters to this guarantor are now blocked");
      onSaved?.();
    } catch (e) { toast.error(err(e, "Could not save")); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="pg-insolvency-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm">Log bankruptcy / IVA</DialogTitle>
          <DialogDescription className="text-xs">Letters and enforcement against this guarantor are blocked from now; the matching sub-timeline opens on their track at the same time.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-xs">
          <div>
            <Label className="text-xs">Guarantor</Label>
            {people.length > 0 ? (
              <Select value={people.includes(form.guarantor_name) ? form.guarantor_name : "__other"} onValueChange={(v) => set("guarantor_name", v === "__other" ? "" : v)}>
                <SelectTrigger className="h-8 mt-1" data-testid="pg-insolvency-party"><SelectValue placeholder="Pick a person on the map" /></SelectTrigger>
                <SelectContent>{people.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}<SelectItem value="__other">Someone else…</SelectItem></SelectContent>
              </Select>
            ) : null}
            {(!people.length || !people.includes(form.guarantor_name)) && <Input value={form.guarantor_name} onChange={(e) => set("guarantor_name", e.target.value)} placeholder="Full name as on the PG — added to the map" className="h-8 mt-1" data-testid="pg-insolvency-name" />}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={form.insolvency_type} onValueChange={(v) => set("insolvency_type", v)}>
                <SelectTrigger className="h-8 mt-1" data-testid="pg-insolvency-type"><SelectValue /></SelectTrigger>
                <SelectContent>{types.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Date of order / arrangement</Label><Input type="date" value={form.date} max={today()} onChange={(e) => set("date", e.target.value)} className="h-8 mt-1" data-testid="pg-insolvency-date" /></div>
            <div><Label className="text-xs">Case / court reference</Label><Input value={form.case_reference} onChange={(e) => set("case_reference", e.target.value)} placeholder="e.g. BR-2026-000123" className="h-8 mt-1" data-testid="pg-insolvency-ref" /></div>
            <div><Label className="text-xs">Trustee / IP / supervisor</Label><Input value={form.practitioner} onChange={(e) => set("practitioner", e.target.value)} className="h-8 mt-1" data-testid="pg-insolvency-ip" /></div>
          </div>
          <div><Label className="text-xs">Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1 text-xs" data-testid="pg-insolvency-notes" /></div>
          {hint && <div className="rounded border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-[11px] text-slate-700" data-testid="pg-insolvency-map-hint"><span className="font-semibold">On the map:</span> {hint}</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" className="bg-red-700 hover:bg-red-800" disabled={busy} onClick={save} data-testid="pg-insolvency-save">{busy && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}Log insolvency</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DischargeDialog({ caseRow, rec, onClose, onDone }) {
  const [sub, setSub] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [outcome, setOutcome] = useState("");
  const [on, setOn] = useState(today());
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/legal/pg-insolvency/${caseRow.case_id}/${rec.id}/map-outcomes`).then((r) => { setSub(r.data.sub); setOutcome(r.data.sub?.default_outcome_id || ""); }).catch(() => {}).finally(() => setLoaded(true));
  }, [caseRow.case_id, rec.id]);
  const go = async () => {
    setBusy(true);
    try {
      const r = (await api.post(`/legal/pg-insolvency/${caseRow.case_id}/${rec.id}/discharge`, { discharged_on: on, notes, outcome_id: outcome || null })).data;
      toast.success(`Marked discharged${r.map?.summary ? ` — ${r.map.summary}` : ""}`);
      onDone?.();
    } catch (e) { toast.error(err(e, "Failed")); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="pg-insolvency-discharge-dialog">
        <DialogHeader><DialogTitle className="text-sm">Discharged / ended — {rec.guarantor_name}</DialogTitle><DialogDescription className="text-xs">{rec.type_label}{rec.case_reference ? ` · ${rec.case_reference}` : ""}. Letters to this guarantor are allowed again.</DialogDescription></DialogHeader>
        <div className="space-y-2 text-xs">
          <label className="block">Date<Input type="date" value={on} max={today()} onChange={(e) => setOn(e.target.value)} className="h-8 mt-1 w-44" data-testid="pg-insolvency-discharge-date" /></label>
          {!loaded ? <div className="text-slate-500 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> checking the map…</div> : sub?.status === "open" ? (
            <label className="block">How it ends on the map — {sub.name} on {sub.party_name}'s {sub.track_name} track
              <Select value={outcome} onValueChange={setOutcome}>
                <SelectTrigger className="h-8 mt-1" data-testid="pg-insolvency-discharge-outcome"><SelectValue /></SelectTrigger>
                <SelectContent>{sub.outcomes.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </label>
          ) : <div className="text-slate-500">{sub ? `${sub.name} is already resolved on the map` : "Nothing open on the map for this record"}</div>}
          <label className="block">Note<Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1 text-xs" data-testid="pg-insolvency-discharge-notes" /></label>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" className="bg-slate-800 hover:bg-slate-900" disabled={busy || !loaded} onClick={go} data-testid="pg-insolvency-discharge-confirm"><CheckCircle2 className="w-3 h-3 mr-1" /> Mark discharged</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function InsolvencyListDialog({ caseRow, onClose, onChanged, onLog }) {
  const [discharge, setDischarge] = useState(null);
  const act = activeInsolvencies(caseRow);
  const past = (caseRow?.pg_insolvencies || []).filter((r) => r.discharged_at);
  const remove = async (r) => {
    if (!window.confirm(`Remove the insolvency record for ${r.guarantor_name}? Only do this if it was logged in error — what it recorded on the map stays (undo that from History).`)) return;
    try { await api.delete(`/legal/pg-insolvency/${caseRow.case_id}/${r.id}`); toast.success("Removed"); onChanged?.(); } catch (e) { toast.error(err(e, "Failed")); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="pg-insolvency-list-dialog">
        <DialogHeader><DialogTitle className="text-sm flex items-center gap-2"><UserX className="w-4 h-4 text-red-700" /> Guarantor insolvency — {act.length} active</DialogTitle><DialogDescription className="text-xs">Letters refuse to address these guarantors unless the override is ticked. Pursue the company and any other guarantors as normal.</DialogDescription></DialogHeader>
        <ul className="space-y-1.5 text-xs" data-testid="pg-insolvency-list">
          {act.map((r) => (
            <li key={r.id} className="rounded border border-red-200 bg-red-50/60 px-2.5 py-1.5" data-testid={`pg-insolvency-row-${r.id}`}>
              <div className="flex flex-wrap items-center gap-x-2">
                <span className="font-semibold text-red-900">{r.guarantor_name}</span> · {r.type_label}{r.case_reference ? <> · ref <span className="font-mono">{r.case_reference}</span></> : null} · {ukDate(r.date)}{r.practitioner && <> · {r.practitioner}</>}
                <span className="ml-auto flex items-center gap-2">
                  <button type="button" className="underline text-[11px]" onClick={() => setDischarge(r)} data-testid={`pg-insolvency-discharge-${r.id}`}><CheckCircle2 className="inline w-3 h-3" /> discharged</button>
                  <button type="button" className="underline text-[11px] text-slate-600" onClick={() => remove(r)} data-testid={`pg-insolvency-remove-${r.id}`}><Trash2 className="inline w-3 h-3" /> remove</button>
                </span>
              </div>
              <div className="text-[10px] text-red-800/80">{r.map_link?.summary ? `Map: ${r.map_link.summary}` : "Not recorded on the map"}{r.notes ? ` — ${r.notes}` : ""} · logged by {r.recorded_by?.split("@")[0]} {ukDate(r.recorded_at)}</div>
            </li>
          ))}
          {!act.length && <li className="text-slate-500 italic">None active.</li>}
          {past.map((r) => <li key={r.id} className="text-slate-500 px-2.5" data-testid={`pg-insolvency-past-${r.id}`}>{r.guarantor_name} · {r.type_label} · discharged {ukDate(r.discharged_on || r.discharged_at)}{r.map_discharge?.summary ? ` · ${r.map_discharge.summary}` : ""}</li>)}
        </ul>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
          <Button size="sm" className="bg-red-700 hover:bg-red-800" onClick={onLog} data-testid="pg-insolvency-list-log">Log another</Button>
        </DialogFooter>
        {discharge && <DischargeDialog caseRow={caseRow} rec={discharge} onClose={() => setDischarge(null)} onDone={() => { setDischarge(null); onChanged?.(); }} />}
      </DialogContent>
    </Dialog>
  );
}

export function InsolvencyChip({ caseRow, onClick }) {
  const act = activeInsolvencies(caseRow);
  if (!act.length) return null;
  return (
    <button type="button" onClick={onClick} className="text-[10px] px-1.5 py-px rounded bg-red-900/40 text-red-50 border border-red-200/60 hover:bg-red-900/60 flex items-center gap-1" title={act.map((r) => `${r.guarantor_name}: ${r.type_label}`).join("\n")} data-testid="legal-map-insolvency-chip">
      <AlertTriangle className="w-2.5 h-2.5" /> Insolvency: {act.length} active
    </button>
  );
}
